/**
 * Theme Registry System
 * Manages theme discovery, loading, and caching
 */
import { PageDefinition, ThemeDesignSystem, ThemeManifest, ThemeMode } from './types';

export class ThemeRegistry {
  private static instance: ThemeRegistry;
  private themes: Map<string, ThemeManifest> = new Map();
  private designCache: Map<string, ThemeDesignSystem> = new Map();
  private activeThemeId: string | null = null;

  private constructor() {}

  static getInstance(): ThemeRegistry {
    if (!ThemeRegistry.instance) {
      ThemeRegistry.instance = new ThemeRegistry();
    }
    return ThemeRegistry.instance;
  }

  /**
   * Register a theme in the registry
   */
  registerTheme(manifest: ThemeManifest): void {
    this.themes.set(manifest.id, manifest);
  }

  /**
   * Get all registered themes
   */
  getThemes(): ThemeManifest[] {
    return Array.from(this.themes.values());
  }

  /**
   * Get a specific theme manifest
   */
  getTheme(themeId: string): ThemeManifest | undefined {
    return this.themes.get(themeId);
  }

  /**
   * Set the active theme
   */
  setActiveTheme(themeId: string): void {
    if (!this.themes.has(themeId)) {
      throw new Error(`Theme "${themeId}" is not registered`);
    }
    this.activeThemeId = themeId;
  }

  /**
   * Get the active theme ID
   */
  getActiveThemeId(): string | null {
    return this.activeThemeId;
  }

  /**
   * Get the active theme manifest
   */
  getActiveTheme(): ThemeManifest | null {
    if (!this.activeThemeId) return null;
    return this.themes.get(this.activeThemeId) || null;
  }

  /**
   * Cache design system
   */
  cacheDesign(themeId: string, mode: ThemeMode, design: ThemeDesignSystem): void {
    const key = `${themeId}:${mode}`;
    this.designCache.set(key, design);
  }

  /**
   * Get cached design system
   */
  getCachedDesign(themeId: string, mode: ThemeMode): ThemeDesignSystem | undefined {
    const key = `${themeId}:${mode}`;
    return this.designCache.get(key);
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.designCache.clear();
  }

  /**
   * Validate theme manifest
   */
  validateManifest(manifest: Partial<ThemeManifest>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!manifest.id) errors.push('Theme ID is required');
    if (!manifest.name) errors.push('Theme name is required');
    if (!manifest.version) errors.push('Theme version is required');

    if (!manifest.capabilities) {
      errors.push('Theme capabilities are required');
    } else {
      if (!manifest.capabilities.modes || manifest.capabilities.modes.length === 0) {
        errors.push('At least one theme mode is required');
      }
      if (!manifest.capabilities.languages || manifest.capabilities.languages.length === 0) {
        errors.push('At least one language is required');
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Get the singleton registry instance
 */
export const getThemeRegistry = () => ThemeRegistry.getInstance();
